import { useRef, useEffect, useState, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  useSlotGroup,
  usePropertyField,
  useContentToken,
  useVibesites,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { rewriteBedroomRangeDisplay } from '../utils/bedroomLabel';
import { resolveHref } from '../utils/resolveHref';
import { useCtaColorVars } from '../hooks/useCtaColorVars';
import { onColorLinksCss } from '../utils/onColorLinks';

/** Page-level props shared by `HeroSolidBG` and the per-page hero slots. */
export interface HeroSolidBGContentProps {
  title?: string;
  /** SEO-friendly richtext subtitle shown above the page title. */
  subtitle?: string;
  /** Set false to hide the SEO subtitle (e.g. on utility pages). */
  showSubtitle?: boolean;
  blurbField?: string;
  defaultText?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  /** Set false to hide the CTA button (e.g. on the Amenities hero). */
  showCta?: boolean;
  /** Set false to hide the blurb entirely (e.g. on pages whose body copy
   *  already carries the lead paragraph). */
  showBlurb?: boolean;
}

interface HeroSolidBGProps extends HeroSolidBGContentProps {
  tokenPrefix?: string;
}

interface HeroSolidBGViewProps extends HeroSolidBGContentProps {
  /** First path segment for every token / slot group this hero renders. */
  prefix: string;
  /** Definition for `${prefix}.bg_image` (per-page slots add a generator). */
  bgDefinition: SlotGroupDefinition;
}

// Background image slot group, mirroring the homepage Hero's `featuredImageDef`
// so this page hero renders the same parallax + Ken Burns photo treatment.
const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const LOCATION_SUFFIX =
  ' in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>';

const BEDS_PREFIX =
  '<span data-type="field-token" data-field="property_bedrooms_range"></span> ';

/** Default SEO subtitle when a page does not pass `subtitle` via pages.json. */
function defaultSubtitleForTitle(title: string): string {
  return `<p>${BEDS_PREFIX}${title}${LOCATION_SUFFIX}</p>`;
}

function HeroSolidBGSubtitle({
  path,
  defaultValue,
}: {
  path: string;
  defaultValue: string;
}) {
  const { property } = useVibesites();
  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const rawHtml = useContentToken(path, { defaultValue });

  const resolvedHtml = useMemo(() => {
    const source = rawHtml || defaultValue;
    const resolved = source
      ? resolveRichText(source, (property ?? {}) as Record<string, unknown>)
      : '';
    const rewritten = rewriteBedroomRangeDisplay(
      resolved,
      bedroomsRange as string | null,
      { prependIfMissing: false },
    );
    // Outer EditableToken is already a <p>; unwrap a single wrapping <p>.
    const match = rewritten.match(/^<p>([\s\S]*)<\/p>$/);
    return match ? match[1] : rewritten;
  }, [rawHtml, defaultValue, property, bedroomsRange]);

  return (
    <EditableToken
      path={path}
      type="richtext"
      label="Subtitle"
      as="p"
      className="hero-solid-subtitle-font w-full max-w-2xl sm:max-w-3xl md:max-w-4xl lg:max-w-5xl text-sm sm:text-base md:text-lg lg:text-xl leading-snug tracking-[0.04em] opacity-90 drop-shadow-md uppercase text-balance pointer-events-auto"
    >
      <span
        data-vibesite-richtext=""
        dangerouslySetInnerHTML={{ __html: resolvedHtml }}
        style={{ display: 'contents' }}
      />
    </EditableToken>
  );
}

/** Schema-level CTA definition (the view builds a per-instance copy so its
 *  defaults follow the `ctaLabel` / `ctaUrl` props). */
export const heroSolidCtaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Floor Plans',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function HeroSolidBGView({
  prefix,
  bgDefinition,
  title = 'Page',
  subtitle,
  showSubtitle = true,
  blurbField,
  defaultText,
  ctaLabel = 'View Floor Plans',
  ctaUrl = '/floor-plans',
  showCta = true,
  showBlurb = true,
}: HeroSolidBGViewProps) {
  const titlePath = `${prefix}.title`;
  const subtitlePath = `${prefix}.subtitle`;
  const blurbPath = `${prefix}.blurb`;
  const ctaPath = `${prefix}.cta_link`;
  const bgPath = `${prefix}.bg_image`;
  const subtitleDefault = subtitle || defaultSubtitleForTitle(title);
  const color4 = usePropertyField('property_color_4')?.trim();
  const ctaColorVars = useCtaColorVars();

  // The default URL tracks the default CTA label so the link target matches
  // the verb ("View Floor Plans" → /floor-plans). Each page in pages.json
  // is expected to pass `ctaLabel` and `ctaUrl` together when overriding;
  // property_apply_now_url / property_website_url remain available to
  // editors via suggestedFields.
  const ctaLinkDef: SlotGroupDefinition = {
    label: 'CTA Link',
    fields: {
      url: {
        label: 'URL',
        type: 'url',
        defaultValue: ctaUrl,
        suggestedFields: ['property_apply_now_url', 'property_website_url'],
      },
      label: {
        label: 'Link Text',
        type: 'text',
        defaultValue: ctaLabel,
      },
      _target: {
        label: 'Target',
        type: 'text',
        defaultValue: '_self',
      },
    },
  };

  const cta = useSlotGroup(ctaPath, ctaLinkDef);
  const bgImage = useSlotGroup(bgPath, bgDefinition);

  // Parallax refs — the background image shifts at 30 % of scroll speed to
  // create depth, matching the homepage Hero.
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // "Read more" behaviour for the blurb — on mobile only, a long blurb is
  // collapsed to ~half its natural height (with a soft fade) behind a toggle
  // so it never fills the banner. From the `sm` breakpoint up the full blurb
  // always shows and no toggle renders.
  const blurbRef = useRef<HTMLDivElement>(null);
  const [blurbExpanded, setBlurbExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [blurbFullHeight, setBlurbFullHeight] = useState<number | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const el = blurbRef.current;
    if (!el) return;
    // scrollHeight reports the full content height regardless of the collapsed
    // maxHeight/overflow, so this stays accurate whether or not it's clamped.
    const measure = () => setBlurbFullHeight(el.scrollHeight);
    measure();
    window.addEventListener('resize', measure);
    // Re-measure once webfonts settle — the mono blurb face changes line height.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    return () => window.removeEventListener('resize', measure);
  }, [isMobile, blurbField, defaultText]);

  // Only offer the toggle when the mobile blurb is tall enough to be worth
  // truncating (~8+ lines); short blurbs render in full with no button.
  const showBlurbToggle =
    isMobile && blurbFullHeight != null && blurbFullHeight > 160;
  const blurbCollapsed = showBlurbToggle && !blurbExpanded;
  const collapsedHeight =
    blurbFullHeight != null ? Math.round(blurbFullHeight * 0.5) : 0;

  return (
    <div className="relative w-full z-10 bg-[var(--vibe-color-background)]">
      <section
        ref={sectionRef}
        className="relative w-full overflow-hidden text-white"
        style={{
          // Downward-pointing chevron: the bottom edge slopes from each side
          // down to a single point at the horizontal centre, revealing the
          // white page background as two triangular wedges. clip-path also
          // clips the oversized parallax image, replacing overflow-hidden.
          //
          // The section no longer sets its own height — the centred content
          // block below owns it via a min-height floor (max(98vh,720px)) and
          // grows to fit long titles/blurbs. The chevron percentages therefore
          // track the actual rendered height. Centered content clears it: the
          // deepest cut under the (horizontally centred) copy is the tip at
          // 90%, and the 22vh bottom padding keeps the last line clear of it.
          clipPath: 'polygon(0 0, 100% 0, 100% 76%, 50% 90%, 0 76%)',
        }}
      >
        {/* Parallax + Ken Burns animated background */}
        <EditableSlotGroup groupPath={bgPath} definition={bgDefinition}>
          {/* Oversized wrapper: 140 % tall starting 20 % above the section top,
              and ~2 % wider than the section (1 % bleed on each side). The
              extra height gives the parallax translate room to move, and the
              extra width absorbs the Ken Burns horizontal translate. */}
          <div
            ref={imgWrapRef}
            className="absolute h-[140%]"
            style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
          >
            {bgImage.image ? (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                priority
                sizes="100vw"
                className="w-full h-full object-cover"
                style={{
                  animation: 'heroSolidKenBurns 30s ease-in-out infinite alternate',
                  willChange: 'transform',
                  transformOrigin: 'bottom right',
                  imageRendering: 'high-quality' as any,
                }}
              />
            ) : (
              <div className="w-full h-full bg-[var(--vibe-color-primary)]" />
            )}
          </div>
        </EditableSlotGroup>

        {/* Primary-color overlay at 77% opacity, layered above the parallax
            image but below the content (z-10) so text and CTA stay legible. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 z-0 pointer-events-none"
          style={{ background: 'var(--vibe-color-primary)', opacity: 0.77 }}
        />

        {/* Content — sits in the image’s safe band (below the fixed header,
            above the chevron cut). Top padding clears SpecialOfferBanner +
            desktop util row (h-8) + main nav (h-16 / lg:h-20) with room to
            breathe so subtitle never tucks under the logo/nav. */}
        <div
          className="relative z-10 flex flex-col items-center justify-center min-h-[max(98vh,720px)] lg:min-h-[max(92vh,680px)] text-center px-5 sm:px-8 lg:px-12 pointer-events-none hero-solid-content"
          style={{
            paddingTop: 'calc(var(--offer-banner-height, 0px) + 6rem)',
            paddingBottom: '26vh',
          }}
        >
          {/* Compact copy stack — SEO subtitle above the page title (per
              brief), then blurb. Narrower desktop measure so long uppercase
              SEO lines + title + blurb stay a single composed block. */}
          <div className="hero-solid-copy flex w-full max-w-2xl sm:max-w-3xl md:max-w-4xl lg:max-w-5xl flex-col items-center gap-2 sm:gap-3 lg:gap-3.5 pointer-events-none">
            {/* SEO subtitle — wider measure than the blurb on tablet/desktop
                so it wraps later (fewer line breaks on long SEO lines). */}
            {showSubtitle && (
              <HeroSolidBGSubtitle path={subtitlePath} defaultValue={subtitleDefault} />
            )}

            {/* Page Title */}
            <div className="min-w-0 w-full max-w-2xl sm:max-w-3xl lg:max-w-4xl pointer-events-auto">
              <RichTextContent
                path={titlePath}
                defaultValue={`<p>${title}</p>`}
                as="h1"
                className="hero-solid-title-font text-2xl sm:text-4xl md:text-5xl lg:text-[3.25rem] xl:text-5xl font-normal uppercase tracking-normal sm:tracking-wider leading-[1.05] break-words hyphens-auto text-center drop-shadow-lg text-balance"
              />
            </div>

            {/* Blurb — wider wrap measure on tablet/desktop (closer to the
                subtitle column) so long property blurbs break later. Pages
                may opt out of the blurb via `showBlurb={false}` when the
                page body already carries the lead paragraph. */}
            {showBlurb && (
              <div className="w-full max-w-xl sm:max-w-2xl md:max-w-3xl lg:max-w-4xl mt-0.5 sm:mt-1 pointer-events-auto">
                <div
                  ref={blurbRef}
                  style={
                    blurbCollapsed
                      ? {
                          maxHeight: `${collapsedHeight}px`,
                          overflow: 'hidden',
                          WebkitMaskImage:
                            'linear-gradient(to bottom, #000 55%, transparent 100%)',
                          maskImage:
                            'linear-gradient(to bottom, #000 55%, transparent 100%)',
                        }
                      : undefined
                  }
                >
                  <RichTextContent
                    path={blurbPath}
                    defaultField={blurbField}
                    defaultValue={defaultText || ''}
                    as="div"
                    className="hero-solid-blurb-font text-base font-normal not-italic leading-relaxed text-white opacity-90 [&_a]:underline text-center drop-shadow-md"
                  />
                </div>
                {showBlurbToggle && (
                  <button
                    type="button"
                    onClick={() => setBlurbExpanded((v) => !v)}
                    aria-expanded={blurbExpanded}
                    className="hero-solid-readmore font-heading text-xs uppercase tracking-wider underline underline-offset-4 mt-3 sm:hidden"
                  >
                    {blurbExpanded ? 'Read Less' : 'Read More'}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* CTA */}
          {showCta && (
            <div className="text-center mt-10 sm:mt-12 md:mt-14 pointer-events-auto">
              <EditableSlotGroup groupPath={ctaPath} definition={ctaLinkDef}>
                <a
                  href={resolveHref(cta.url || '#')}
                  target={cta._target || '_self'}
                  className="hero-solid-cta-btn group/cta relative inline-flex items-center gap-2 px-10 py-4 font-heading text-sm uppercase tracking-wider overflow-hidden transition-all duration-300"
                  style={ctaColorVars}
                >
                  <span className="relative z-10">{cta.label || ctaLabel}</span>
                </a>
              </EditableSlotGroup>
            </div>
          )}
        </div>

        <style>{`
          ${onColorLinksCss('.hero-solid-copy')}
          @media (min-width: 1024px) {
            .hero-solid-content {
              /* Desktop header = util row (2rem) + nav (5rem). Clear that
                 plus a gap so the SEO subtitle never overlaps the header. */
              padding-top: calc(var(--offer-banner-height, 0px) + 9.25rem) !important;
              padding-bottom: 30vh !important;
            }
          }
          /* Title + subtitle typeface: Nunito Sans (loaded by the Header via
             the css2 link; not in the editor font picker), applied to the
             element and all of its rich-text children. */
          .hero-solid-title-font,
          .hero-solid-title-font * {
            font-family: 'Nunito Sans', sans-serif;
            font-weight: 400;
          }
          .hero-solid-subtitle-font,
          .hero-solid-subtitle-font * {
            font-family: 'Nunito Sans', sans-serif;
          }
          /* Match FloorplansList's feature_body (.fp-feature-body-font):
             Space Mono at normal weight/style, applied to the element and
             all of its rich-text children. */
          .hero-solid-blurb-font,
          .hero-solid-blurb-font * {
            font-family: var(--vibe-font-body);
            font-weight: 400;
            font-style: normal;
            /* Pin the blurb (and every rich-text child) to a single size so it
               renders identically on every inner-page header. On mobile the
               monospace copy is dropped to 0.75rem with tighter line spacing
               so subtitle + title + blurb stay inside the image band; it steps
               back up at the sm breakpoint. */
            font-size: 0.75rem;
            line-height: 1.45;
            /* Property blurbs sometimes ship with CMS inline color / highlight
               backgrounds (white boxes behind dark text). Strip those so every
               page matches the clean white-on-overlay treatment. */
            color: #fff !important;
            background: transparent !important;
            background-color: transparent !important;
          }
          @media (min-width: 640px) {
            .hero-solid-blurb-font,
            .hero-solid-blurb-font * {
              font-size: 0.875rem;
              line-height: 1.5;
            }
          }
          @media (min-width: 1024px) {
            .hero-solid-blurb-font,
            .hero-solid-blurb-font * {
              /* Keep desktop blurb compact so subtitle + title + body share
                 the image band without crowding the chevron. */
              font-size: 0.9375rem;
              line-height: 1.55;
            }
          }
          /* Read More toggle — subtle white link over the primary overlay,
             brightening on hover; matches the heading (Oswald) family. */
          .hero-solid-readmore {
            color: #fff;
            opacity: 0.85;
            cursor: pointer;
            transition: opacity 0.2s ease;
          }
          .hero-solid-readmore:hover {
            opacity: 1;
          }
          @keyframes heroSolidKenBurns {
            0% { transform: scale(1.01) translate(0, 0); }
            100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
          }
          /* Mirrors the homepage Hero's .hero-cta: a solid secondary fill that
             inverts to white on hover, sitting over the image + overlay. */
          .hero-solid-cta-btn {
            background: var(--cta-bg, var(--vibe-color-secondary));
            border: 1.5px solid var(--cta-bg, var(--vibe-color-secondary));
            border-radius: 2px;
            color: var(--cta-text, #fff);
            box-shadow: none;
          }
          .hero-solid-cta-btn:hover {
            background: #fff;
            border-color: #fff;
            color: var(--cta-bg, var(--vibe-color-secondary));
            transform: translateY(-2px);
            box-shadow: none;
          }
          .hero-solid-cta-btn:active {
            transform: translateY(0);
            box-shadow: none;
          }
        `}</style>
      </section>

      {/* Vertical accent line centred on the chevron tip — the clip-path dips
          to its lowest point (90% of the hero height) at the horizontal centre,
          which is exactly where the image meets the white space along this
          column. Lives in the wrapper (not the clipped section) so the chevron
          clip-path doesn't cut it off. */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 -translate-x-1/2 w-[3px] h-[16%] z-20"
        style={{ background: color4 || 'var(--vibe-color-secondary)', bottom: '2%' }}
      />
    </div>
  );
}

export function HeroSolidBG({ tokenPrefix, ...props }: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  return (
    <>
      <HeroSolidBGView {...props} prefix={prefix} bgDefinition={featuredImageDef} />

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableToken
        path="herosolidbg.subtitle"
        type="richtext"
        label="Subtitle"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={featuredImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.cta_link"
        definition={heroSolidCtaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_bedrooms_range"></span> Apartments for Rent in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.bg_image': featuredImageDef,
    'herosolidbg.cta_link': heroSolidCtaLinkDef,
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
